import { md5 } from './md5';
var meta = function (selector) { var _a; return ((_a = document.querySelector(selector)) === null || _a === void 0 ? void 0 : _a.content) || undefined; };
var canonical = function () {
    var _a;
    return ((_a = document.querySelector('link[rel="canonical"]')) === null || _a === void 0 ? void 0 : _a.href) ||
        undefined;
};
/**
 * Resolve the current page's product, read **fresh from the DOM** on every call
 * (no persisted global — safe across SPA navigations). Used as the fallback for
 * `trackPageView()` and as the seed for item-to-item recommendations.
 *
 * Resolution order for the id:
 *   1. `<meta name="cdpf:product" content="<id>">` (explicit opt-in)
 *   2. `product:retailer_item_id` (or `og:product:retailer_item_id`) on an Open
 *      Graph product page (`og:type` is `product` or `*product`).
 *   3. md5 of the product URL, so a product page missing a retailer id still
 *      gets a stable id instead of being dropped (mirrors `resolveCurrentContent`).
 *
 * Optional attributes are taken from the standard Open Graph product meta when
 * available. Returns undefined on non-product pages so listings never attach a
 * product (and therefore never produce recommender "view" feedback).
 */
export function resolveCurrentProduct() {
    var customId = meta('meta[name="cdpf:product"]');
    var ogType = meta('meta[property="og:type"]');
    var ogId = meta('meta[property="product:retailer_item_id"]') ||
        meta('meta[property="og:product:retailer_item_id"]');
    var isOgProduct = !!ogType && /(^|\.)product$/.test(ogType);
    // Only resolve a product on an explicit opt-in or a real product page, so
    // listings never attach a phantom product.
    if (!customId && !isOgProduct)
        return undefined;
    var url = meta('meta[property="og:url"]') || canonical() || window.location.href;
    var id = customId || ogId || md5(url);
    var priceRaw = meta('meta[property="product:price:amount"]');
    var price = priceRaw != null ? Number(priceRaw) : undefined;
    return {
        id: id,
        price: price != null && Number.isFinite(price) ? price : undefined,
        currency: meta('meta[property="product:price:currency"]'),
        brand: meta('meta[property="product:brand"]'),
        category: meta('meta[property="product:category"]') ||
            meta('meta[property="article:section"]'),
        // Descriptive fields for catalogue indexing, read from the page meta.
        name: meta('meta[property="og:title"]') || document.title || undefined,
        description: meta('meta[property="og:description"]') || undefined,
        image: meta('meta[property="og:image"]') || undefined,
        url: url,
    };
}
/** Convenience for callers that only need the current product id. */
export function resolveCurrentProductId() {
    var _a;
    return (_a = resolveCurrentProduct()) === null || _a === void 0 ? void 0 : _a.id;
}
/**
 * Resolve the current page's editorial content (article), fresh from the DOM.
 * Id source: `<meta name="cdpf:content">`, else an Open Graph article page
 * (`og:type` = `article`) keyed by `og:url` or the canonical URL. Returns
 * undefined on non-article pages.
 */
export function resolveCurrentContent() {
    var customId = meta('meta[name="cdpf:content"]');
    var ogType = meta('meta[property="og:type"]');
    var isOgArticle = !!ogType && /(^|\.)article$/.test(ogType);
    // Only resolve content on an explicit opt-in or a real article page, so
    // listings never attach a phantom article.
    if (!customId && !isOgArticle)
        return undefined;
    // Canonical article URL, kept as a body field. Falls back to the current
    // location so md5 always has a stable input.
    var url = meta('meta[property="og:url"]') || canonical() || window.location.href;
    // Opaque, stable id: an explicit cdpf:content id when provided, otherwise the
    // md5 of the URL (so the same article maps to one catalogue document).
    var id = customId || md5(url);
    return {
        id: id,
        url: url,
        title: meta('meta[property="og:title"]') || document.title || undefined,
        description: meta('meta[property="og:description"]') || undefined,
        image: meta('meta[property="og:image"]') || undefined,
        category: meta('meta[property="article:section"]') ||
            meta('meta[name="cdpf:content-category"]'),
    };
}
/**
 * The current page's recommendable item id (product OR article), used as the
 * real-time seed for item-to-item recommendations regardless of kind.
 */
export function resolveCurrentItemId() {
    var _a, _b;
    return ((_a = resolveCurrentProduct()) === null || _a === void 0 ? void 0 : _a.id) || ((_b = resolveCurrentContent()) === null || _b === void 0 ? void 0 : _b.id);
}
/**
 * Resolve the page's language as a lowercased BCP-47 primary subtag (e.g. `en`,
 * `it`), read from `<html lang>` (or the `og:locale` meta as a fallback). Used
 * to route product/article catalogue ingestion to the correct per-language
 * index. Returns `'en'` when nothing is declared.
 */
export function resolvePageLang() {
    var htmlLang = document.documentElement.getAttribute('lang') || undefined;
    var ogLocale = meta('meta[property="og:locale"]');
    var raw = (htmlLang || ogLocale || '').trim();
    // Keep only the primary language subtag: "en-US" / "it_IT" -> "en" / "it".
    var primary = raw.split(/[-_]/)[0].toLowerCase();
    return primary || 'en';
}
